import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { NeedsSetup } from "@/components/NeedsSetup";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Vote, Plus, Building2, Briefcase, Calendar, IndianRupee, 
  CheckCircle, Loader2, Users, Scale
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Voting = () => {
  const [developers, setDevelopers] = useState([]);
  const [voteResults, setVoteResults] = useState(null);
  const [society, setSociety] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [voting, setVoting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedDeveloper, setSelectedDeveloper] = useState(null);
  const [voteType, setVoteType] = useState("simple");
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    experience_years: "",
    past_projects: "",
    offer_details: "",
    corpus_amount: "",
    rent_amount: "",
    timeline_months: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const socRes = await fetch(`${API}/societies`, { credentials: "include" });
      const societies = await socRes.json();
      
      if (societies.length > 0) {
        setSociety(societies[0]);
        
        const [devsRes, votesRes, membersRes] = await Promise.all([
          fetch(`${API}/societies/${societies[0].society_id}/developers`, { credentials: "include" }),
          fetch(`${API}/societies/${societies[0].society_id}/votes`, { credentials: "include" }),
          fetch(`${API}/societies/${societies[0].society_id}/members`, { credentials: "include" })
        ]);
        
        if (devsRes.ok) setDevelopers(await devsRes.json());
        if (votesRes.ok) setVoteResults(await votesRes.json());
        if (membersRes.ok) setMembers(await membersRes.json());
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddDeveloper = async (e) => {
    e.preventDefault();
    if (!society) return;

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        company: formData.company,
        experience_years: parseInt(formData.experience_years) || 0,
        past_projects: parseInt(formData.past_projects) || 0,
        offer_details: formData.offer_details,
        corpus_amount: formData.corpus_amount ? parseFloat(formData.corpus_amount) : null,
        rent_amount: formData.rent_amount ? parseFloat(formData.rent_amount) : null,
        timeline_months: formData.timeline_months ? parseInt(formData.timeline_months) : null
      };

      const res = await fetch(`${API}/societies/${society.society_id}/developers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include"
      });

      if (res.ok) {
        const newDev = await res.json();
        setDevelopers([...developers, newDev]);
        toast.success("Developer added successfully");
        setDialogOpen(false);
        setFormData({
          name: "",
          company: "",
          experience_years: "",
          past_projects: "",
          offer_details: "",
          corpus_amount: "",
          rent_amount: "",
          timeline_months: ""
        });
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to add developer");
      }
    } catch (error) {
      toast.error("Failed to add developer");
    } finally {
      setSaving(false);
    }
  };

  const handleVote = async () => {
    if (!selectedDeveloper || !society) {
      toast.error("Please select a developer");
      return;
    }

    setVoting(true);
    try {
      const res = await fetch(`${API}/societies/${society.society_id}/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          developer_id: selectedDeveloper,
          vote_type: voteType
        }),
        credentials: "include"
      });

      if (res.ok) {
        toast.success("Vote recorded successfully");
        fetchData();
        setSelectedDeveloper(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to vote");
      }
    } catch (error) {
      toast.error("Failed to vote");
    } finally {
      setVoting(false);
    }
  };

  const getVoteCount = (developerId, type) => {
    if (!voteResults) return 0;
    if (type === "simple") {
      return voteResults.simple_votes?.[developerId] || 0;
    }
    return ((voteResults.weighted_votes?.[developerId] || 0) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!society) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <NeedsSetup message="Set up your society to start developer voting." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Developer Voting
            </h1>
            <p className="text-slate-600">Select your preferred developer through transparent voting</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-developer-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Developer
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope' }}>Add Developer Candidate</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddDeveloper} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Contact Person *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g., Amit Shah"
                      required
                      data-testid="dev-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company">Company Name *</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="e.g., ABC Developers"
                      required
                      data-testid="dev-company-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="experience_years">Experience (Years)</Label>
                    <Input
                      id="experience_years"
                      name="experience_years"
                      type="number"
                      value={formData.experience_years}
                      onChange={handleChange}
                      placeholder="e.g., 15"
                      data-testid="dev-exp-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="past_projects">Past Projects</Label>
                    <Input
                      id="past_projects"
                      name="past_projects"
                      type="number"
                      value={formData.past_projects}
                      onChange={handleChange}
                      placeholder="e.g., 25"
                      data-testid="dev-projects-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="corpus_amount">Corpus (₹ Lakhs)</Label>
                    <Input
                      id="corpus_amount"
                      name="corpus_amount"
                      type="number"
                      value={formData.corpus_amount}
                      onChange={handleChange}
                      placeholder="e.g., 25"
                      data-testid="dev-corpus-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="rent_amount">Rent (₹/month)</Label>
                    <Input
                      id="rent_amount"
                      name="rent_amount"
                      type="number"
                      value={formData.rent_amount}
                      onChange={handleChange}
                      placeholder="e.g., 30000"
                      data-testid="dev-rent-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="timeline_months">Timeline (Months)</Label>
                    <Input
                      id="timeline_months"
                      name="timeline_months"
                      type="number"
                      value={formData.timeline_months}
                      onChange={handleChange}
                      placeholder="e.g., 36"
                      data-testid="dev-timeline-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="offer_details">Offer Details *</Label>
                  <Textarea
                    id="offer_details"
                    name="offer_details"
                    value={formData.offer_details}
                    onChange={handleChange}
                    placeholder="Describe the developer's offer including area ratio, amenities, etc."
                    rows={3}
                    required
                    data-testid="dev-offer-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-emerald-700 hover:bg-emerald-800"
                  disabled={saving}
                  data-testid="submit-developer-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Developer
                    </>
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Legal weight disclaimer */}
        <div className="flex gap-3 p-4 rounded-lg border border-amber-300 bg-amber-50" data-testid="voting-legal-disclaimer">
          <Scale className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">
            <strong>In-app voting is supporting documentation only.</strong> It does not replace your society's
            legally required AGM/SGM resolution under the applicable co-operative housing laws. Please conduct
            the formal society resolution separately; use these results to inform and record member preference.
          </p>
        </div>

        {/* Voting Type Selector */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Voting Method</CardTitle>
            <CardDescription>Choose how votes should be counted</CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup value={voteType} onValueChange={setVoteType} className="flex gap-6">
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="simple" id="simple" data-testid="simple-vote-radio" />
                <div>
                  <Label htmlFor="simple" className="font-medium cursor-pointer flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-600" />
                    Simple Voting
                  </Label>
                  <p className="text-xs text-slate-500">One vote per member</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="weighted" id="weighted" data-testid="weighted-vote-radio" />
                <div>
                  <Label htmlFor="weighted" className="font-medium cursor-pointer flex items-center gap-2">
                    <Scale className="w-4 h-4 text-slate-600" />
                    Weighted Voting
                  </Label>
                  <p className="text-xs text-slate-500">Vote weight based on flat area</p>
                </div>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Developers List */}
        {developers.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-8 h-8 text-emerald-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                No Developers Added
              </h3>
              <p className="text-slate-600 max-w-md mx-auto mb-6">
                Add developer candidates who have submitted proposals for your society's redevelopment.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {developers.map((dev) => (
              <Card 
                key={dev.developer_id}
                className={`vote-card ${selectedDeveloper === dev.developer_id ? 'selected' : ''}`}
                onClick={() => setSelectedDeveloper(dev.developer_id)}
                data-testid={`developer-card-${dev.developer_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                        {dev.company}
                      </h3>
                      <p className="text-sm text-slate-500">Contact: {dev.name}</p>
                    </div>
                    {selectedDeveloper === dev.developer_id && (
                      <CheckCircle className="w-6 h-6 text-emerald-600" />
                    )}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <Briefcase className="w-4 h-4 text-slate-500 mx-auto mb-1" />
                      <p className="text-lg font-bold text-slate-900">{dev.experience_years || '-'}</p>
                      <p className="text-xs text-slate-500">Years Exp.</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <Building2 className="w-4 h-4 text-slate-500 mx-auto mb-1" />
                      <p className="text-lg font-bold text-slate-900">{dev.past_projects || '-'}</p>
                      <p className="text-xs text-slate-500">Projects</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <Calendar className="w-4 h-4 text-slate-500 mx-auto mb-1" />
                      <p className="text-lg font-bold text-slate-900">{dev.timeline_months || '-'}</p>
                      <p className="text-xs text-slate-500">Months</p>
                    </div>
                  </div>

                  <div className="flex gap-4 mb-4 text-sm">
                    {dev.corpus_amount && (
                      <div className="flex items-center gap-1">
                        <IndianRupee className="w-3 h-3 text-emerald-600" />
                        <span className="text-slate-700">{dev.corpus_amount}L corpus</span>
                      </div>
                    )}
                    {dev.rent_amount && (
                      <div className="flex items-center gap-1">
                        <IndianRupee className="w-3 h-3 text-emerald-600" />
                        <span className="text-slate-700">{dev.rent_amount.toLocaleString()}/mo rent</span>
                      </div>
                    )}
                  </div>

                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">{dev.offer_details}</p>

                  {/* Vote Counts */}
                  <div className="border-t border-slate-100 pt-4 flex justify-between text-sm">
                    <div>
                      <span className="text-slate-500">Simple Votes:</span>
                      <span className="font-medium text-slate-900 ml-2">
                        {getVoteCount(dev.developer_id, "simple")}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Weighted:</span>
                      <span className="font-medium text-slate-900 ml-2">
                        {getVoteCount(dev.developer_id, "weighted")}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Vote Button */}
        {developers.length > 0 && (
          <div className="flex justify-center">
            <Button
              onClick={handleVote}
              disabled={!selectedDeveloper || voting}
              className="bg-orange-600 hover:bg-orange-700 px-8"
              size="lg"
              data-testid="cast-vote-btn"
            >
              {voting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Recording Vote...
                </>
              ) : (
                <>
                  <Vote className="w-4 h-4 mr-2" />
                  Cast {voteType === "simple" ? "Simple" : "Weighted"} Vote
                </>
              )}
            </Button>
          </div>
        )}

        {/* Voting Stats */}
        {voteResults && voteResults.total_members > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Voting Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-8">
                <div>
                  <p className="text-sm text-slate-500">Total Members</p>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                    {voteResults.total_members}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Simple Votes Cast</p>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                    {Object.values(voteResults.simple_votes || {}).reduce((a, b) => a + b, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Weighted Votes Cast</p>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                    {(Object.values(voteResults.weighted_votes || {}).reduce((a, b) => a + b, 0) * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};
